// frontend/src/components/AdminPanel.js
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { productsAPI, reportsAPI } from '../services/api';
import { Settings, Plus, Package, Edit, CheckCircle, XCircle, Trash2, Save, X, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

function AdminPanel() {
  const [activeSection, setActiveSection] = useState('products');
  const queryClient = useQueryClient();

  const sections = [
    { id: 'products', label: 'Produkter', icon: Package },
    { id: 'variants', label: 'Varianter', icon: Settings },
    { id: 'reports', label: 'Rapporter', icon: FileText }
  ];

  return (
    <div className="space-y-6">
      {/* Section Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Settings className="mr-2" size={24} />
            Administration
          </h2>
        </div>
        
        <div className="p-6">
          <div className="flex space-x-4 mb-6">
            {sections.map(section => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    activeSection === section.id
                      ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Icon size={18} />
                  <span>{section.label}</span>
                </button>
              );
            })}
          </div>

          {activeSection === 'products' && <ProductsSection />}
          {activeSection === 'variants' && <VariantsSection />}
          {activeSection === 'reports' && <ReportsSection />}
        </div>
      </div>
    </div>
  );
}

// Products Section Component
function ProductsSection() {
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    baseProperties: ''
  });

  const queryClient = useQueryClient();

  const { data: products, isLoading } = useQuery('products', productsAPI.getAll, {
    select: data => data.data
  });

  const createProductMutation = useMutation(productsAPI.create, {
    onSuccess: () => {
      toast.success('Produkt oprettet succesfuldt');
      resetForm();
      queryClient.invalidateQueries('products');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Fejl ved oprettelse af produkt');
    }
  });

  const updateProductMutation = useMutation(
    ({ id, data }) => productsAPI.update(id, data),
    {
      onSuccess: () => {
        toast.success('Produkt opdateret succesfuldt');
        setEditingProduct(null);
        queryClient.invalidateQueries('products');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Fejl ved opdatering af produkt');
      }
    }
  );

  const deleteProductMutation = useMutation(
    (productId) => productsAPI.delete(productId),
    {
      onSuccess: () => {
        toast.success('Produkt slettet succesfuldt');
        queryClient.invalidateQueries('products');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Fejl ved sletning af produkt');
      }
    }
  );

  const resetForm = () => {
    setFormData({ name: '', description: '', category: '', baseProperties: '' });
    setShowForm(false);
    setEditingProduct(null);
  };

  const handleEdit = (product) => {
    setEditingProduct(product.id);
    setFormData({
      name: product.name,
      description: product.description || '',
      category: product.category,
      baseProperties: product.baseProperties ? JSON.stringify(product.baseProperties, null, 2) : ''
    });
    setShowForm(false);
  };

  const handleDelete = (product) => {
    if (window.confirm(`Er du sikker på at du vil slette "${product.name}"?\n\nDette vil også slette alle varianter af produktet.`)) {
      deleteProductMutation.mutate(product.id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.category) {
      toast.error('Udfyld alle påkrævede felter');
      return;
    }

    let baseProperties = {};
    if (formData.baseProperties) {
      try {
        baseProperties = JSON.parse(formData.baseProperties);
      } catch (error) {
        toast.error('Ugyldig JSON i egenskaber');
        return;
      }
    }

    const productData = {
      name: formData.name,
      description: formData.description,
      category: formData.category,
      baseProperties
    };

    if (editingProduct) {
      updateProductMutation.mutate({ id: editingProduct, data: productData });
    } else {
      createProductMutation.mutate(productData);
    }
  };

  const categories = [
    'Betonelementer',
    'Kemi'
  ];

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Produkter</h3>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingProduct(null);
            setFormData({ name: '', description: '', category: '', baseProperties: '' });
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
        >
          <Plus size={16} />
          <span>Nyt Produkt</span>
        </button>
      </div>

      {/* Create/Edit Product Form */}
      {(showForm || editingProduct) && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">
            {editingProduct ? 'Rediger Produkt' : 'Opret Nyt Produkt'}
          </h4>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Produktnavn *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="f.eks. Betonklods Standard"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kategori *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Vælg kategori...</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Beskrivelse
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="2"
                placeholder="Beskrivelse af produktet..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Egenskaber (JSON format)
              </label>
              <textarea
                value={formData.baseProperties}
                onChange={(e) => setFormData({...formData, baseProperties: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder='{"styrke": "C25/30", "frostbestandig": true}'
              />
              <p className="text-xs text-gray-500 mt-1">
                Valgfrit: JSON format med produktegenskaber
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={createProductMutation.isLoading || updateProductMutation.isLoading}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {createProductMutation.isLoading || updateProductMutation.isLoading 
                  ? (editingProduct ? 'Opdaterer...' : 'Opretter...') 
                  : (editingProduct ? 'Opdater Produkt' : 'Opret Produkt')
                }
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Annuller
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Products List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <h4 className="font-medium text-gray-900">Eksisterende Produkter</h4>
        </div>
        
        {isLoading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : products && products.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {products.map(product => (
              <div key={product.id} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">{product.name}</h5>
                    <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                    <div className="flex items-center space-x-4 mt-2">
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                        {product.category}
                      </span>
                      <span className="text-xs text-gray-500">
                        {product.variants?.length || 0} varianter
                      </span>
                      <span className="text-xs text-gray-500">
                        ID: {product.id.slice(0, 8)}...
                      </span>
                    </div>
                    {product.baseProperties && Object.keys(product.baseProperties).length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500">Egenskaber:</p>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {JSON.stringify(product.baseProperties)}
                        </code>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {product.isActive ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                    
                    {/* Edit Button */}
                    <button
                      onClick={() => handleEdit(product)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      title="Rediger produkt"
                    >
                      <Edit size={16} />
                    </button>
                    
                    {/* Delete Button */}
                    <button
                      onClick={() => handleDelete(product)}
                      disabled={deleteProductMutation.isLoading}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                      title="Slet produkt"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            Ingen produkter endnu. Opret dit første produkt ovenfor.
          </div>
        )}
      </div>
    </div>
  );
}

// Variants Section Component
function VariantsSection() {
  const [selectedProduct, setSelectedProduct] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [formData, setFormData] = useState({
    sku: '',
    length: '',
    width: '',
    height: '',
    løfteøjer: false,
    gaffellommer: false,
    armering: '',
    curingTimeHours: '24',
    weight: '',
    price: ''
  });

  const queryClient = useQueryClient();

  const { data: products } = useQuery('products', productsAPI.getAll, {
    select: data => data.data
  });

  const { data: variants, isLoading: variantsLoading } = useQuery(
    ['variants', selectedProduct],
    () => selectedProduct ? productsAPI.getVariants(selectedProduct) : Promise.resolve({ data: [] }),
    {
      enabled: !!selectedProduct,
      select: data => data.data
    }
  );

  const createVariantMutation = useMutation(
    (data) => productsAPI.createVariant(selectedProduct, data),
    {
      onSuccess: () => {
        toast.success('Variant oprettet succesfuldt');
        resetForm();
        queryClient.invalidateQueries(['variants', selectedProduct]);
        queryClient.invalidateQueries('products');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Fejl ved oprettelse af variant');
      }
    }
  );

  const updateVariantMutation = useMutation(
    ({ variantId, data }) => productsAPI.updateVariant(selectedProduct, variantId, data),
    {
      onSuccess: () => {
        toast.success('Variant opdateret succesfuldt');
        resetForm();
        queryClient.invalidateQueries(['variants', selectedProduct]);
        queryClient.invalidateQueries('products');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Fejl ved opdatering af variant');
      }
    }
  );

  const deleteVariantMutation = useMutation(
    (variantId) => productsAPI.deleteVariant(selectedProduct, variantId),
    {
      onSuccess: (response) => {
        const { deletedBatches = 0, affectedOrders = 0 } = response.data || {};
        let message = 'Variant slettet succesfuldt';
        if (deletedBatches > 0) {
          message += ` (${deletedBatches} produktionsbatch${deletedBatches !== 1 ? 'es' : ''} slettet)`;
        }
        toast.success(message);
        queryClient.invalidateQueries(['variants', selectedProduct]);
        queryClient.invalidateQueries('products');
        queryClient.invalidateQueries('stock');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Fejl ved sletning af variant');
      }
    }
  );

  const resetForm = () => {
    setFormData({
      sku: '', length: '', width: '', height: '',
      løfteøjer: false, gaffellommer: false, armering: '',
      curingTimeHours: '24', weight: '', price: ''
    });
    setShowForm(false);
    setEditingVariant(null);
  };

  const handleEdit = (variant) => {
    setEditingVariant(variant.id);
    setFormData({
      sku: variant.sku,
      length: variant.dimensions.length.toString(),
      width: variant.dimensions.width.toString(),
      height: variant.dimensions.height.toString(),
      løfteøjer: variant.properties?.løfteøjer || false,
      gaffellommer: variant.properties?.gaffellommer || false,
      armering: variant.properties?.armering || '',
      curingTimeHours: variant.curingTimeHours.toString(),
      weight: variant.weight ? variant.weight.toString() : '',
      price: variant.price ? variant.price.toString() : ''
    });
    setShowForm(false);
  };

  const handleDelete = (variant) => {
    if (window.confirm(`Er du sikker på at du vil slette varianten "${variant.sku}"?\n\n⚠️ ADVARSEL: Dette vil:\n- Fjerne varianten fra lageroversigten\n- Slette ALLE produktionsbatches for denne variant\n- Fjerne alt lager for denne variant\n\nDenne handling kan ikke fortrydes!`)) {
      deleteVariantMutation.mutate(variant.id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedProduct) {
      toast.error('Vælg et produkt først');
      return;
    }

    if (!formData.sku || !formData.length || !formData.width || !formData.height) {
      toast.error('Udfyld alle påkrævede felter');
      return;
    }

    const variantData = {
      sku: formData.sku,
      dimensions: {
        length: parseInt(formData.length),
        width: parseInt(formData.width),
        height: parseInt(formData.height)
      },
      properties: {
        løfteøjer: formData.løfteøjer,
        gaffellommer: formData.gaffellommer,
        armering: formData.armering
      },
      curingTimeHours: parseInt(formData.curingTimeHours),
      weight: formData.weight ? parseFloat(formData.weight) : null,
      price: formData.price ? parseFloat(formData.price) : null
    };

    if (editingVariant) {
      updateVariantMutation.mutate({ variantId: editingVariant, data: variantData });
    } else {
      createVariantMutation.mutate(variantData);
    }
  };

  const selectedProductInfo = products?.find(p => p.id === selectedProduct);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Produktvarianter</h3>
        {selectedProduct && (
          <button
            onClick={() => {
              setShowForm(!showForm);
              setEditingVariant(null);
              setFormData({
                sku: '', length: '', width: '', height: '',
                løfteøjer: false, gaffellommer: false, armering: '',
                curingTimeHours: '24', weight: '', price: ''
              });
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            <Plus size={16} />
            <span>Ny Variant</span>
          </button>
        )}
      </div>

      {/* Product Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Vælg Produkt
        </label>
        <select
          value={selectedProduct}
          onChange={(e) => {
            setSelectedProduct(e.target.value);
            resetForm();
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Vælg et produkt...</option>
          {products?.map(product => (
            <option key={product.id} value={product.id}>
              {product.name} - {product.category}
            </option>
          ))}
        </select>
      </div>

      {selectedProduct && selectedProductInfo && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900">Valgt Produkt:</h4>
          <p className="text-blue-800">{selectedProductInfo.name}</p>
          <p className="text-sm text-blue-600">{selectedProductInfo.description}</p>
        </div>
      )}

      {/* Create/Edit Variant Form */}
      {(showForm || editingVariant) && selectedProduct && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">
            {editingVariant ? 'Rediger Variant' : 'Opret Ny Variant'}
          </h4>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SKU (Produktkode) *
              </label>
              <input
                type="text"
                value={formData.sku}
                onChange={(e) => setFormData({...formData, sku: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="f.eks. BK-30x30x30"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Længde (cm) *
                </label>
                <input
                  type="number"
                  value={formData.length}
                  onChange={(e) => setFormData({...formData, length: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bredde (cm) *
                </label>
                <input
                  type="number"
                  value={formData.width}
                  onChange={(e) => setFormData({...formData, width: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Højde (cm) *
                </label>
                <input
                  type="number"
                  value={formData.height}
                  onChange={(e) => setFormData({...formData, height: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="løfteøjer"
                    checked={formData.løfteøjer}
                    onChange={(e) => setFormData({...formData, løfteøjer: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="løfteøjer" className="text-sm text-gray-700">
                    Løfteøjer
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="gaffellommer"
                    checked={formData.gaffellommer}
                    onChange={(e) => setFormData({...formData, gaffellommer: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="gaffellommer" className="text-sm text-gray-700">
                    Gaffellommer
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Armering
                </label>
                <input
                  type="text"
                  value={formData.armering}
                  onChange={(e) => setFormData({...formData, armering: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="f.eks. Standard stålarmering"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hærdetid (timer)
                </label>
                <input
                  type="number"
                  value={formData.curingTimeHours}
                  onChange={(e) => setFormData({...formData, curingTimeHours: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vægt (kg)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.weight}
                  onChange={(e) => setFormData({...formData, weight: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pris (DKK)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={createVariantMutation.isLoading || updateVariantMutation.isLoading}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {createVariantMutation.isLoading || updateVariantMutation.isLoading
                  ? (editingVariant ? 'Opdaterer...' : 'Opretter...')
                  : (editingVariant ? 'Opdater Variant' : 'Opret Variant')
                }
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Annuller
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Variants List */}
      {selectedProduct && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <h4 className="font-medium text-gray-900">Varianter for {selectedProductInfo?.name}</h4>
          </div>
          
          {variantsLoading ? (
            <div className="p-6 text-center">Loading...</div>
          ) : variants && variants.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {variants.map(variant => (
                <div key={variant.id} className="p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900">{variant.sku}</h5>
                      <p className="text-sm text-gray-600 mt-1">
                        {variant.dimensions.length}×{variant.dimensions.width}×{variant.dimensions.height} cm
                      </p>
                      <div className="flex items-center space-x-4 mt-2">
                        {variant.properties?.løfteøjer && (
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                            Løfteøjer
                          </span>
                        )}
                        {variant.properties?.gaffellommer && (
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            Gaffellommer
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {variant.curingTimeHours}t hærdning
                        </span>
                        {variant.weight && (
                          <span className="text-xs text-gray-500">
                            {variant.weight} kg
                          </span>
                        )}
                        {variant.price && (
                          <span className="text-xs text-gray-500">
                            {variant.price} DKK
                          </span>
                        )}
                      </div>
                      {variant.properties?.armering && (
                        <p className="text-xs text-gray-500 mt-1">
                          Armering: {variant.properties.armering}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {variant.isActive ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                      
                      {/* Edit Button */}
                      <button
                        onClick={() => handleEdit(variant)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        title="Rediger variant"
                      >
                        <Edit size={16} />
                      </button>
                      
                      {/* Delete Button */}
                      <button
                        onClick={() => handleDelete(variant)}
                        disabled={deleteVariantMutation.isLoading}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                        title="Slet variant"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              Ingen varianter for dette produkt endnu.
              {selectedProduct && ' Opret din første variant ovenfor.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Reports Section Component
function ReportsSection() {
  const today = new Date().toISOString().split('T')[0];
  const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString()
    .split('T')[0];

  const [startDate, setStartDate] = useState(firstDayOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Vælg både start- og slutdato');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Startdato skal være før slutdato');
      return;
    }

    setIsDownloading(true);

    try {
      const response = await reportsAPI.downloadInventoryReport(startDate, endDate);

      // Opret blob fra response data
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Opret download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Lageroversigt_${startDate}_til_${endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Rapport downloadet succesfuldt');
    } catch (error) {
      console.error('Fejl ved download af rapport:', error);
      toast.error(error.response?.data?.error || 'Kunne ikke generere rapport');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Lageroversigt Rapport</h3>
      </div>

      {/* Report Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="space-y-4">
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">
              Vælg periode for rapport
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Rapporten viser total lagerstatus samt produktion og salg for den valgte periode.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fra dato
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Til dato
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                max={today}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="pt-4">
            <button
              onClick={handleDownloadReport}
              disabled={isDownloading || !startDate || !endDate}
              className="w-full md:w-auto flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText size={18} />
              <span>{isDownloading ? 'Genererer rapport...' : 'Download Rapport (Excel)'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Information Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Rapport indhold</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Total på lager:</strong> Summen af tilgængelige og hærdende produkter</li>
          <li>• <strong>Total produceret i perioden:</strong> Antal produkter produceret mellem de valgte datoer</li>
          <li>• <strong>Antal solgte i perioden:</strong> Antal produkter solgt mellem de valgte datoer</li>
        </ul>
        <p className="text-xs text-blue-600 mt-3">
          Rapporten viser data for alle produktvarianter og kan åbnes i Microsoft Excel eller lignende programmer.
        </p>
      </div>
    </div>
  );
}

export default AdminPanel;